/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionDeselectBreadcrumbNavigationLeafNode';

const ActionDeselectBreadcrumbNavigationLeafNodeManager: ActionManagerFn<
  'ActionDeselectBreadcrumbNavigationLeafNode'
> = (
  platform: Platform,
): ActionManager<'ActionDeselectBreadcrumbNavigationLeafNode'> => {
  return {
    ...baseActionManager(
      platform,
      'ActionDeselectBreadcrumbNavigationLeafNode',
    ),
    defaultValue,
    props: {
      ...baseActionProps,
      targetItemId: {
        label: 'Breadcrumb Navigation Item ID',
        type: 'ItemID',
        initialFilter: 'BreadcrumbNavigation',
        required: true,
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (item?.type !== 'BreadcrumbNavigation') {
            return `Item id '${value}' doesn't belong to a Breadcrumb Navigation component.`;
          }
          return '';
        },
      },
    },
    name: 'Deselect Breadcrumb Navigation Leaf Node',
    getDisplayText: (item) => item.props.targetItemId,
    icon: { iconName: 'check-square' },
    description: `**Overview**<br/>
The "Deselect Breadcrumb Navigation Leaf Node" action is a specific UI state management component designed to interact with a Breadcrumb Navigation element. Its primary function is to programmatically clear the selection state of the "leaf node"—the final item in the breadcrumb trail—effectively reverting that specific segment of the navigation to an unselected state.<br/><br/>
This action is tightly coupled to the BreadcrumbNavigation component type. It includes strict validation logic to ensure it only operates on the correct component type, preventing runtime errors that could occur from targeting unrelated UI elements.

**Key Features**<br/>
* **Targeted State Modification:** Specifically focuses on the last level (leaf node) of the breadcrumb hierarchy, leaving parent nodes unaffected.
* **Strict Type Validation:** Implements validateItemId to verify that the provided targetItemId corresponds to a component of type 'BreadcrumbNavigation', returning an error message if a mismatch occurs.
* **Registry Filtering:** Uses initialFilter: 'BreadcrumbNavigation' to streamline the configuration process, restricting the property picker to only show relevant breadcrumb components.

**Properties**<br/>
* **Breadcrumb Navigation Item ID (targetItemId):** An ItemID property that identifies the specific Breadcrumb Navigation component to update.
  * *Required:* Yes.
  * *Validation:* Checks if the item exists and is of type BreadcrumbNavigation.

**Use Cases**<br/>
* **Contextual Reset:** Unselecting the current page/view in the breadcrumb bar when closing a related detail panel or drawer without navigating away from the parent category.
* **Visual Cleanup:** Removing the "active" highlight from the last breadcrumb item when the user performs an action that invalidates that specific context.`,
  };
};

export default ActionDeselectBreadcrumbNavigationLeafNodeManager;
